import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, PanInfo, useDragControls } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  headerAction?: React.ReactNode;
  /** Pinned below the scrollable content, above the home indicator. */
  footer?: React.ReactNode;
  /** "full" takes almost the whole screen; "auto" sizes to content. */
  size?: "auto" | "full";
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
}

const DISMISS_OFFSET = 120;
const DISMISS_VELOCITY = 600;

const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  description,
  headerAction,
  footer,
  size = "auto",
  className,
  contentClassName,
  children,
}) => {
  const dragControls = useDragControls();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_OFFSET || info.velocity.y > DISMISS_VELOCITY) onClose();
  };

  const startDrag = (event: React.PointerEvent) => dragControls.start(event);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="bottom-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            key="bottom-sheet"
            role="dialog"
            aria-modal="true"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 420, damping: 40 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={handleDragEnd}
            className={cn(
              "fixed inset-x-0 bottom-0 z-[101] flex flex-col rounded-t-3xl bg-white shadow-2xl",
              size === "full" ? "h-[94dvh]" : "max-h-[92dvh]",
              className,
            )}
          >
            <div onPointerDown={startDrag} className="touch-none cursor-grab active:cursor-grabbing">
              <div className="flex justify-center pt-3 pb-1">
                <span className="h-1.5 w-10 rounded-full bg-gray-200" />
              </div>
            </div>

            {(title || headerAction) && (
              <div
                onPointerDown={startDrag}
                className="touch-none flex items-start justify-between gap-3 px-5 pt-1 pb-3"
              >
                <div className="min-w-0 flex-1">
                  {title && (
                    <h2 className="text-lg font-bold font-jakarta text-gray-900 leading-tight">
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p className="mt-1 text-sm text-gray-500">{description}</p>
                  )}
                </div>
                <div
                  className="flex shrink-0 items-center gap-2"
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {headerAction}
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex h-9 w-9 items-center justify-center rounded-full border-0 bg-gray-50 p-0 text-gray-600 active:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto overscroll-contain",
                !footer && "pb-[env(safe-area-inset-bottom)]",
                contentClassName,
              )}
            >
              {children}
            </div>

            {footer && (
              <div className="shrink-0 border-t border-gray-100 bg-white px-5 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default BottomSheet;
